<i18n>
{
  "fr": {
    "change-lang-btn": "English"
  },
  "en": {
    "change-lang-btn": "Français"
  }
}
</i18n>

<template>
  <div class="welcome">
    <div class="container welcome__container">
      <div class="welcome__content">
        <!-- Section blanche -->
        <div class="login">
          <div class="login__header">
            <span class="login__logo">Plateforme Core</span>
            <div class="login__lang is-hidden-tablet">
              <b-button type="is-link" size="is-small" outlined @click="changeLang">{{ $t('change-lang-btn') }}</b-button>
            </div>
          </div>
          <div class="login__inner">
            <slot></slot>
          </div>
        </div>
      </div>

      <!-- Section bleue -->
      <div class="welcome__side-bg"></div>
      <div class="welcome__side">
        <div class="welcome__side-container">
          <div class="welcome__side-inner has-text-white">
            <div class="welcome__side-lang is-hidden-mobile">
              <b-button type="is-link" size="is-small" inverted outlined @click="changeLang">
                {{ $t('change-lang-btn') }}
              </b-button>
            </div>

            <prismic-content :doctype="DOCUMENT_TYPE_LOGIN_PAGE" v-slot="{ content }">
              <prismic-rich-text class="content is-list-style-check has-text-white" :field="content.right_section_text" />
            </prismic-content>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DOCUMENT_TYPE_LOGIN_PAGE } from '@/consts/prismic'
import i18n from '@/helpers/i18n'

import PrismicContent from '@/components/prismic/prismic-content'
import AuthentificationService from '@/services/authentification'

export default {
  components: { PrismicContent },
  data() {
    return {
      DOCUMENT_TYPE_LOGIN_PAGE
    }
  },
  computed: {
    currentUserId() {
      return this.me ? this.me.id : null
    },
    currentLanguageUser() {
      return this.me ? this.me.profile.language : null
    }
  },
  methods: {
    changeLang() {
      i18n.changeLang()
      this.setProfileLanguage()
    },
    async setProfileLanguage() {
      if (!this.currentUserId) return

      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateUserProfile,
        variables: {
          userId: this.currentUserId,
          language: { value: i18n.userLanguage() }
        }
      })

      this.$success()
    }
  },
  watch: {
    currentUserId() {
      if (this.currentUserId) {
        this.$nextTick(() => {
          i18n.setContentLanguageToUserLanguage(this.currentLanguageUser)
        })
      }
    }
  },
  apollo: {
    me: {
      query() {
        return this.$options.query.MyCurrentProfile
      },
      skip() {
        const isLoggedIn = AuthentificationService.getUserIsLogged()
        return !isLoggedIn
      }
    }
  }
}
</script>
<graphql>
query MyCurrentProfile {
  me {
    id
    profile {
      id
      language
    }
  }
}

mutation UpdateUserProfile($userId: ID!, $language: UserLanguageMaybe) {
  updateUserProfile(input: { userId: $userId, language: $language }) {
    user {
      id
      profile {
        id
        language
      }
    }
  }
}
</graphql>
<style lang="scss">
.welcome {
  &__container {
    position: static;
  }

  &__side {
    background-color: $primary;
    position: relative;

    &::before {
      background: url(~@/assets/welcome-background.png) no-repeat center;
      background-size: cover;
      content: '';
      height: 100%;
      left: 0;
      opacity: 0.3;
      position: absolute;
      top: 0;
      width: 100%;
    }

    // Hotfix for bulma content
    .content {
      // Fix for color white
      &.has-text-white * {
        color: white;
      }

      // Display liste with check icon
      ul {
        margin: $gap 0;
        list-style: none;

        li {
          margin-bottom: 12px;
          padding-left: 40px;
          position: relative;

          &::before {
            content: 'done';
            direction: ltr;
            display: inline-block;
            font-family: 'Material Icons';
            font-size: 24px;
            font-style: normal;
            font-weight: normal;
            left: 0;
            letter-spacing: normal;
            line-height: 1;
            position: absolute;
            text-transform: none;
            top: 0;
            white-space: nowrap;
            word-wrap: normal;
            -webkit-font-feature-settings: 'liga';
            -webkit-font-smoothing: antialiased;
            font-feature-settings: 'liga' 1; // For IE11
          }
        }
      }
    }

    &-bg {
      display: none;
    }

    &-container {
      margin: 0 auto;
      max-width: 400px;
      padding: $gap;
      position: relative;
    }
  }

  &__list {
    margin-bottom: $gap;

    &-item {
      margin-bottom: 12px;
      padding-left: 40px;
      position: relative;

      i {
        left: 0;
        position: absolute;
        top: 0;
      }
    }
  }
}

@include tablet {
  .welcome {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    position: relative;
    width: 100%;

    &__container {
      display: flex;
      min-height: inherit;
      width: 100%;
    }

    &__content {
      width: 50%;

      &-header {
        max-width: 400px;
      }

      &-body {
        max-width: 400px;
      }

      &-footer {
        height: 52px;
      }
    }

    &__side {
      align-items: center;
      background-color: transparent;
      display: flex;
      width: 50%;

      &::before {
        display: none;
      }

      &-bg {
        background-color: $primary;
        bottom: 0;
        display: block;
        position: absolute;
        right: 0;
        top: 0;
        width: calc(((100% - (#{$tablet})) / 2) + ((50 * (#{$tablet})) / 100));

        &::before {
          background-image: url(~@/assets/welcome-background.png);
          background-position: 50%;
          background-size: cover;
          content: '';
          height: 100%;
          left: 0;
          opacity: 0.3;
          position: absolute;
          top: 0;
          width: 100%;
        }
      }

      &-container {
        align-items: center;
        display: flex;
        height: 100%;
        padding: 20% $gap;
        position: relative;
        width: 100%;
      }

      &-lang {
        position: absolute;
        right: 20px;
        top: 7%;
      }

      &-inner {
        width: 100%;
      }
    }
  }
}

@include desktop {
  .welcome {
    &__side {
      &-bg {
        width: calc(((100% - (#{$desktop} - (#{$gap} * 2))) / 2) + ((50 * (#{$desktop} - (#{$gap} * 2))) / 100));
      }

      &-container {
        padding: 20% 0;
      }

      &-lang {
        right: 0;
      }
    }
  }
}

@include widescreen {
  .welcome {
    &__side {
      &-bg {
        width: calc(((100% - (#{$widescreen} - (#{$gap} * 2))) / 2) + ((50 * (#{$widescreen} - (#{$gap} * 2))) / 100));
      }
    }
  }
}

@include fullhd {
  .welcome {
    &__side {
      &-bg {
        width: calc(((100% - (#{$fullhd} - (#{$gap} * 2))) / 2) + ((50 * (#{$fullhd} - (#{$gap} * 2))) / 100));
      }
    }
  }
}

.login {
  margin: 0 auto;
  max-width: 400px;
  padding: $gap;

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $gap;
  }

  &__logo {
    background: url(~@/assets/logo-core.svg) no-repeat center center;
    background-size: contain;
    color: transparent;
    display: block;
    font: 0/0 a;
    height: 52px;
    text-shadow: none;
    white-space: nowrap;
    width: 123px;
  }

  &__submit {
    margin: $gap 0;
  }
}

@include tablet {
  .login {
    align-items: center;
    display: flex;
    height: 100%;
    padding: 20% $gap;
    position: relative;
    width: 100%;

    &__header {
      left: 20px;
      margin: 0;
      max-width: 400px;
      position: absolute;
      top: 5%;
    }

    &__inner {
      width: 100%;
    }
  }
}

@include desktop {
  .login {
    padding: 20% 0;

    &__header {
      left: 0;
    }
  }
}
</style>
